import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2 } from "lucide-react";
import { createWorkflowRole, editWorkflowRole, useTypes } from "@/hooks/useLocalStorage";
import { Code } from "@/shared/schema";
import { WorkflowTeamsAction } from "@/lib/api-services";
import { parseApiErrorMessage } from "@/hooks/parseApiErrorMessage";

export type RoleFormValues = {
  roleName: string;
  totalEntry: number;
  decryptData: boolean;
  roleType: number;
  description: string;
  userPermission: boolean;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: WorkflowTeamsAction | null;
  onAlert: (message: string, status: boolean) => void;
};

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label>
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}

export function AddWorkflowPerformerRole({
  open,
  onOpenChange,
  initialData,
  onAlert
}: Props) {
  const isEdit = !!initialData;

  const addWorkflowRoles = createWorkflowRole();
  const editWorkflowRoles = editWorkflowRole();

  const { data: entityTypes, isLoading: entityLoading } = useTypes(
    undefined,
    "WF Role Type"
  );

  const entityOptions =
    entityTypes?.filter(
      (item: Code) => item.entityType === "CODE" && item.useFlag === 1
    ) || [];

  const { control, register, handleSubmit, reset, watch, setValue } =
    useForm<RoleFormValues>({
      defaultValues: {
        roleName: "",
        totalEntry: 0,
        decryptData: false,
        roleType: 0,
        description: "",
        userPermission: false,
      },
    });

  const isRestricted = watch("userPermission");
  const isPending = addWorkflowRoles.isPending || editWorkflowRoles.isPending;

  useEffect(() => {
    if (!open) return;

    if (isEdit && initialData) {
      reset({
        ...initialData,
        totalEntry: initialData.totalEntry ?? 0,
      });
    } else {
      reset({
        roleName: "",
        totalEntry: 0,
        decryptData: false,
        roleType: 0,
        description: "",
        userPermission: false,
      });
    }
  }, [open, initialData]);

  const onSubmit = (data: RoleFormValues) => {
    const payload = {
      ...data,
      totalEntry: data.userPermission ? data.totalEntry : 0,
    };

    if (isEdit) {
      editWorkflowRoles.mutate({ Id: initialData.id, data: payload });
    } else {
      addWorkflowRoles.mutate(payload);
    }
  };

  useEffect(() => {
    if (addWorkflowRoles.isSuccess) {
      onOpenChange(false);
      onAlert("Workflow Performer Roles Created Successfully!", true);
      addWorkflowRoles.reset();
    } else if (addWorkflowRoles.isError) {
      const raw = addWorkflowRoles.error as Error;
      onOpenChange(false);
      onAlert(parseApiErrorMessage(raw.message), false);
      addWorkflowRoles.reset();
    }
  }, [addWorkflowRoles.isSuccess, addWorkflowRoles.isError]);

  useEffect(() => {
    if (editWorkflowRoles.isSuccess) {
      onOpenChange(false);
      onAlert("Workflow Performer Roles Updated Successfully!", true);
      editWorkflowRoles.reset();
    } else if (editWorkflowRoles.isError) {
      const raw = editWorkflowRoles.error as Error;
      onOpenChange(false);
      onAlert(parseApiErrorMessage(raw.message), false);
      editWorkflowRoles.reset();
    }
  }, [editWorkflowRoles.isSuccess, editWorkflowRoles.isError]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{isEdit ? "Edit Role" : "Add Role"}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">

            <Field label="Role Name" required>
              <Input
                placeholder="Enter role name"
                {...register("roleName", { required: true })}
              />
            </Field>

            <Field label="Add Users Permission">
              <Controller
                name="userPermission"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    value={field.value ? "restricted" : "unrestricted"}
                    onValueChange={(val) => {
                      const restricted = val === "restricted";
                      field.onChange(restricted);
                      if (!restricted) setValue("totalEntry", 0);
                    }}
                    className="flex gap-6"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="restricted" id="restricted" />
                      <Label htmlFor="restricted">Restricted</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="unrestricted" id="unrestricted" />
                      <Label htmlFor="unrestricted">Unrestricted</Label>
                    </div>
                  </RadioGroup>
                )}
              />
            </Field>

            {isRestricted && (
              <Field label="Maximum User Count" required>
                <Input
                  type="number"
                  min={0}
                  placeholder="Enter maximum user count"
                  {...register("totalEntry", {
                    required: true,
                    min: 0,
                    valueAsNumber: true,
                  })}
                />
              </Field>
            )}

            <Field label="Permission To Decrypt">
              <Controller
                name="decryptData"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    value={field.value ? "yes" : "no"}
                    onValueChange={(val) => field.onChange(val === "yes")}
                    className="flex gap-6"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="yes" id="decrypt-yes" />
                      <Label htmlFor="decrypt-yes">Yes</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="no" id="decrypt-no" />
                      <Label htmlFor="decrypt-no">No</Label>
                    </div>
                  </RadioGroup>
                )}
              />
            </Field>

            <Field label="Role Type" required>
              <Controller
                name="roleType"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    value={field.value ? String(field.value) : ""}
                    onValueChange={(val) => field.onChange(Number(val))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Role Type">
                        {entityOptions.find((o: Code) => o.id === field.value)?.name ?? ""}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {entityLoading ? (
                        <SelectItem value="loading" disabled>
                          Loading...
                        </SelectItem>
                      ) : (
                        entityOptions.map((type: Code) => (
                          <SelectItem key={type.id} value={String(type.id)}>
                            {type.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                )}
              />
            </Field>

            <Field label="Role Description">
              <Textarea
                placeholder="Enter description"
                className="min-h-[100px]"
                {...register("description")}
              />
            </Field>

            <div className="flex justify-end gap-2">
              <Button type="submit" disabled={isPending}>
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEdit ? "Update" : "Save"}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={isPending}
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

    </>
  );
}